import { Box } from "@chakra-ui/react";
import L from "leaflet";
import React from "react";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png"
});

interface TrafficPoint {
  country: string;
  lat: number;
  lng: number;
  intensity: number;
  visits: number;
}

interface TrafficMapProps {
  data?: TrafficPoint[];
}

// Toronto-focused traffic data points (GTA and surrounding areas)
const defaultTrafficData: TrafficPoint[] = [
  // Downtown Toronto neighborhoods
  {
    country: "Downtown Toronto",
    lat: 43.6532,
    lng: -79.3832,
    intensity: 1.0,
    visits: 15420
  },
  {
    country: "Financial District",
    lat: 43.6426,
    lng: -79.3871,
    intensity: 0.95,
    visits: 12340
  },
  {
    country: "King West",
    lat: 43.6441,
    lng: -79.3957,
    intensity: 0.9,
    visits: 10890
  },
  {
    country: "Entertainment District",
    lat: 43.6465,
    lng: -79.3871,
    intensity: 0.85,
    visits: 9650
  },
  {
    country: "Distillery District",
    lat: 43.6503,
    lng: -79.3598,
    intensity: 0.8,
    visits: 8930
  },
  // North Toronto
  {
    country: "North York",
    lat: 43.7615,
    lng: -79.4111,
    intensity: 0.75,
    visits: 7240
  },
  {
    country: "Yorkville",
    lat: 43.6708,
    lng: -79.3899,
    intensity: 0.7,
    visits: 6180
  },
  {
    country: "Forest Hill",
    lat: 43.6884,
    lng: -79.4103,
    intensity: 0.65,
    visits: 5320
  },
  // East Toronto
  {
    country: "Leslieville",
    lat: 43.6579,
    lng: -79.3389,
    intensity: 0.6,
    visits: 4890
  },
  {
    country: "The Beaches",
    lat: 43.6677,
    lng: -79.2941,
    intensity: 0.55,
    visits: 4120
  },
  {
    country: "Riverdale",
    lat: 43.6648,
    lng: -79.3530,
    intensity: 0.5,
    visits: 3650
  },
  // West Toronto
  {
    country: "Liberty Village",
    lat: 43.6368,
    lng: -79.4197,
    intensity: 0.75,
    visits: 7890
  },
  {
    country: "Junction Triangle",
    lat: 43.6570,
    lng: -79.4678,
    intensity: 0.45,
    visits: 3200
  },
  {
    country: "High Park",
    lat: 43.6533,
    lng: -79.4637,
    intensity: 0.4,
    visits: 2890
  },
  // GTA suburbs
  {
    country: "Mississauga",
    lat: 43.5890,
    lng: -79.6441,
    intensity: 0.8,
    visits: 8650
  },
  {
    country: "Brampton",
    lat: 43.7315,
    lng: -79.7624,
    intensity: 0.65,
    visits: 5890
  },
  {
    country: "Markham",
    lat: 43.8561,
    lng: -79.3370,
    intensity: 0.7,
    visits: 6540
  },
  {
    country: "Richmond Hill",
    lat: 43.8828,
    lng: -79.4403,
    intensity: 0.6,
    visits: 4320
  },
  {
    country: "Vaughan",
    lat: 43.8361,
    lng: -79.4985,
    intensity: 0.55,
    visits: 3890
  },
  {
    country: "Oakville",
    lat: 43.4675,
    lng: -79.6877,
    intensity: 0.65,
    visits: 5120
  },
  {
    country: "Burlington",
    lat: 43.3255,
    lng: -79.7990,
    intensity: 0.5,
    visits: 3450
  },
  {
    country: "Ajax",
    lat: 43.8509,
    lng: -79.0204,
    intensity: 0.45,
    visits: 2890
  },
  {
    country: "Pickering",
    lat: 43.8384,
    lng: -79.0868,
    intensity: 0.4,
    visits: 2340
  },
  {
    country: "Whitby",
    lat: 43.8975,
    lng: -78.9429,
    intensity: 0.35,
    visits: 2100
  }
];

const TrafficMap: React.FC<TrafficMapProps> = ({
  data = defaultTrafficData
}) => {
  // Toronto coordinates as map center (like the Python version)
  const mapCenter: [number, number] = [43.7, -79.42];

  return (
    <Box
      height="500px"
      width="100%"
      borderRadius="lg"
      overflow="hidden"
      border="1px solid"
      borderColor="gray.200"
      bg="white"
      style={{ userSelect: "none" }}
    >
      <MapContainer
        center={mapCenter}
        zoom={7}
        style={{ height: "100%", width: "100%", cursor: "grab" }}
        zoomControl={true}
        scrollWheelZoom="center"
        wheelPxPerZoomLevel={60}
        wheelDebounceTime={40}
        doubleClickZoom={true}
        dragging={true}
        touchZoom={true}
        boxZoom={true}
        keyboard={true}
        maxBoundsViscosity={1.0}
        whenReady={() => {
          // Ensure map is properly initialized
          setTimeout(() => {
            window.dispatchEvent(new Event("resize"));
          }, 100);
        }}
      >
        {/* CartoDB Positron tiles for clean, light styling */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />

        {/* Marker clustering similar to the Python version */}
        <MarkerClusterGroup chunkedLoading singleMarkerMode={true}>
          {data.map((point, index) => (
            <Marker key={index} position={[point.lat, point.lng]}>
              <Popup>
                <div style={{ fontFamily: "system-ui", padding: "4px" }}>
                  <strong>{point.country}</strong>
                  <br />
                  <span style={{ color: "#0959ED", fontWeight: "bold" }}>
                    {point.visits.toLocaleString()}
                  </span>{" "}
                  visits
                  <br />
                  <span style={{ color: "#666", fontSize: "12px" }}>
                    Lat: {point.lat.toFixed(4)}, Lng: {point.lng.toFixed(4)}
                  </span>
                </div>
              </Popup>
            </Marker>
          ))}
        </MarkerClusterGroup>
      </MapContainer>
    </Box>
  );
};

export default TrafficMap;
